import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';

const radioBarVariants = cva('flex max-w-fit items-center justify-center p-px rounded-md transition-colors', {
  variants: {
    variant: {
      // Default/secondary: grey background container (standard RadioBar)
      secondary: 'bg-grey-10 border border-grey-30',
      // Hollow: white background, transparent border (minimal style)
      hollow: 'bg-white border border-transparent',
      // Primary: primary color background (accent style)
      primary: 'bg-primary border border-transparent',
    },
    orientation: {
      horizontal: 'flex-row',
      vertical: 'flex-col',
    },
    size: {
      small: 'h-6.5',
      medium: 'h-7.5',
      large: 'h-8.5',
    },
  },
  defaultVariants: {
    variant: 'secondary',
    orientation: 'horizontal',
    size: 'small',
  },
});

const radioBarItemVariants = cva(
  'flex items-center h-full px-[3px] py-0 rounded transition-all shrink-0 border cursor-pointer',
  {
    variants: {
      variant: {
        secondary: [
          // Unchecked: transparent background, grey icon
          'data-[state=unchecked]:bg-transparent data-[state=unchecked]:border-transparent',
          'data-[state=unchecked]:text-grey-60',
          'data-[state=unchecked]:hover:bg-grey-20',
          'data-[state=unchecked]:active:bg-grey-30',
          // Checked: blue background, blue border, blue-60 text (matches Selected button)
          'data-[state=checked]:bg-blue-10 data-[state=checked]:border-blue-50',
          '[&>*]:data-[state=checked]:text-blue-60',
          'data-[state=checked]:hover:bg-blue-20',
          'data-[state=checked]:active:bg-blue-30',
        ],
        hollow: [
          // Unchecked: transparent background, primary-dark icon
          'data-[state=unchecked]:bg-transparent data-[state=unchecked]:border-transparent',
          'data-[state=unchecked]:text-primary-dark',
          'data-[state=unchecked]:hover:bg-grey-20',
          'data-[state=unchecked]:active:bg-grey-30',
          // Checked: grey background, grey border, primary-dark text
          'data-[state=checked]:bg-grey-20 data-[state=checked]:border-grey-30',
          'data-[state=checked]:text-primary-dark',
          'data-[state=checked]:hover:bg-grey-30',
          'data-[state=checked]:active:bg-grey-40',
        ],
        primary: [
          // Unchecked: transparent background, white icon
          'data-[state=unchecked]:bg-transparent data-[state=unchecked]:border-transparent',
          'data-[state=unchecked]:text-white',
          'data-[state=unchecked]:hover:bg-white/10',
          'data-[state=unchecked]:active:bg-white/20',
          // Checked: blue-10 background, blue-50 border, blue-60 text (matches Selected button)
          'data-[state=checked]:bg-blue-10 data-[state=checked]:border-blue-50',
          'data-[state=checked]:text-blue-60',
          'data-[state=checked]:hover:bg-blue-20',
          'data-[state=checked]:active:bg-blue-30',
        ],
      },
    },
    compoundVariants: [
      // Disabled: no hover/active effects (keep same bg as non-hover)
      {
        variant: 'secondary',
        class:
          'disabled:data-[state=unchecked]:hover:bg-transparent disabled:data-[state=unchecked]:active:bg-transparent disabled:data-[state=checked]:hover:bg-blue-10 disabled:data-[state=checked]:active:bg-blue-10',
      },
      {
        variant: 'hollow',
        class:
          'disabled:data-[state=unchecked]:hover:bg-transparent disabled:data-[state=unchecked]:active:bg-transparent disabled:data-[state=checked]:hover:bg-grey-20 disabled:data-[state=checked]:active:bg-grey-20',
      },
      {
        variant: 'primary',
        class:
          'disabled:data-[state=unchecked]:hover:bg-transparent disabled:data-[state=unchecked]:active:bg-transparent disabled:data-[state=checked]:hover:bg-blue-10 disabled:data-[state=checked]:active:bg-blue-10',
      },
    ],
    defaultVariants: {
      variant: 'secondary',
    },
  },
);

type RadioBarVariant = 'secondary' | 'hollow' | 'primary';

const RadioBarContext = React.createContext<RadioBarVariant>('secondary');

type RadioBarProps = React.ComponentProps<typeof RadioGroupPrimitive.Root> &
  Omit<VariantProps<typeof radioBarVariants>, 'orientation' | 'variant'> & {
    variant?: RadioBarVariant;
    orientation?: 'horizontal' | 'vertical';
  };

function RadioBar({
  className,
  variant = 'secondary',
  orientation = 'horizontal',
  size,
  children,
  ...props
}: RadioBarProps) {
  return (
    <RadioBarContext.Provider value={variant}>
      <RadioGroupPrimitive.Root
        data-slot="radio-bar"
        orientation={orientation}
        className={cn(radioBarVariants({ variant, orientation, size }), className)}
        {...props}
      >
        {children}
      </RadioGroupPrimitive.Root>
    </RadioBarContext.Provider>
  );
}

type RadioBarItemProps = React.ComponentProps<typeof RadioGroupPrimitive.Item> & {
  children: React.ReactNode;
};

function RadioBarItem({ className, children, ...props }: RadioBarItemProps) {
  const variant = React.useContext(RadioBarContext);

  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-bar-item"
      className={cn(
        radioBarItemVariants({ variant }),
        // Focus state (consistent across all variants)
        'focus-ring focus-visible:z-10 focus-visible:relative',
        // Disabled state (consistent across all variants)
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
    </RadioGroupPrimitive.Item>
  );
}

export { RadioBar, RadioBarItem, radioBarVariants, radioBarItemVariants, type RadioBarProps, type RadioBarItemProps };
